import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const SimilarBusinesses = ({ businesses, onSave, savedBusinesses = [] }) => {
  const navigate = useNavigate();

  if (!businesses || businesses?.length === 0) {
    return null;
  }

  const handleBusinessClick = (businessId) => {
    navigate(`/business-detail?id=${businessId}`);
  };

  const handleSaveClick = (e, businessId) => {
    e?.stopPropagation();
    onSave(businessId);
  };

  return (
    <div className="bg-background">
      <h2 className="text-2xl font-heading font-semibold text-foreground mb-6">
        You May Also Like
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {businesses?.map((business) => {
          const isSaved = savedBusinesses?.includes(business?.id);
          
          return (
            <div
              key={business?.id}
              className="bg-card rounded-lg overflow-hidden shadow-organic hover:shadow-organic-md transition-organic cursor-pointer group"
              onClick={() => handleBusinessClick(business?.id)}
            >
              {/* Business Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={business?.image}
                  alt={business?.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-organic"
                />
                
                {/* Save Button */}
                <button
                  onClick={(e) => handleSaveClick(e, business?.id)}
                  className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-all duration-300 ${
                    isSaved
                      ? 'bg-destructive text-white' :'bg-white/80 text-foreground hover:bg-white hover:scale-110'
                  }`}
                  aria-label={isSaved ? 'Remove from saved' : 'Save business'}
                >
                  <Icon 
                    name="Heart" 
                    size={16} 
                    className={isSaved ? 'fill-current' : ''}
                  />
                </button>

                {/* Online Badge */}
                {business?.isOnline && (
                  <div className="absolute top-3 left-3 px-2 py-1 bg-accent text-accent-foreground text-xs font-body rounded-full">
                    Online
                  </div>
                )}
              </div>
              {/* Business Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-foreground group-hover:text-primary transition-gentle line-clamp-1">
                      {business?.name}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body line-clamp-2 mt-1">
                      {business?.shortDescription}
                    </p>
                  </div>
                  
                  {business?.logo && (
                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-border bg-white ml-3 flex-shrink-0">
                      <Image
                        src={business?.logo}
                        alt={`${business?.name} logo`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>

                {/* Location */}
                <div className="flex items-center space-x-1 mb-3">
                  <Icon name="MapPin" size={14} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground font-body">
                    {business?.location}
                  </span>
                </div>

                {/* Categories */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {business?.categories?.slice(0, 2)?.map((category, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-primary/10 text-primary text-xs font-body rounded-full"
                    >
                      {category}
                    </span>
                  ))}
                  {business?.categories?.length > 2 && (
                    <span className="px-2 py-1 bg-muted text-muted-foreground text-xs font-body rounded-full">
                      +{business?.categories?.length - 2} more
                    </span>
                  )}
                </div>

                {/* Rating & Price */}
                <div className="flex items-center justify-between">
                  {business?.rating && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={14} className="text-warning fill-current" />
                      <span className="text-sm font-body font-medium text-foreground">
                        {business?.rating}
                      </span>
                      <span className="text-sm text-muted-foreground font-body">
                        ({business?.reviewCount})
                      </span>
                    </div>
                  )}
                  
                  {business?.priceRange && (
                    <span className="text-sm font-body font-semibold text-primary">
                      {business?.priceRange}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SimilarBusinesses;